import React, {useState} from 'react'
import {connect} from "react-redux";
import {withRouter} from "react-router";
import {API} from "aws-amplify";
import {toast} from "react-toastify";
import Popup from "reactjs-popup";
import _ from "lodash";
import {CircularProgress} from "@mui/material";
import Modal from "@/common/Modal";
import moment from '@/common/momentConfig';
import {SortButton, FilterButton, Button, PaginationExpanderButton} from "@/components/Buttons"
import {AssetButtonSettings} from "@/icons/Assets"
import {fetchDigestSettings} from "@/shared/satisfaction/actions";

import SatisfactionPersonnelCell from "./SatisfactionPersonnelCell"
import AddEditDigestSettings from "./AddEditDigestSettings";
import AddEditSatisfactionFilters from "./AddEditSatisfactionFilters";
import SatisfactionStatistics from "./SatisfactionStatistics";
import {isAiInlineSummarizationEnabled} from "@/shared/access";
import AiSummaryBox from "@/common/AiSummaryBox";

const mapStateToProps = state => ({
    ...state
})

const mapDispatchToProps = dispatch => ({
    getDigestSettings: () => dispatch(fetchDigestSettings())
})

function SatisfactionWithFilterIndex(props) {
    const DATE_FORMAT = "YYYY-MM-DD";
    const DEFAULT_SORT = "NAME ASC";
    let startDateMoment = moment().utc().subtract(30, 'day');
    let endDateMoment = moment().utc();
    let defaultStartDateFormatted = startDateMoment.format(DATE_FORMAT);
    let defaultEndDateFormatted = endDateMoment.format(DATE_FORMAT);
    const [personnel, setPersonnel] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [sortDirection, setSortDirection] = React.useState("ASC");
    const [hasMore, setHasMore] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [filterOptions, setFilterOptions] = React.useState(false);
    const [sort, setSort] = React.useState(DEFAULT_SORT);
    const [filters, setFilters] = React.useState([]);
    const [startDate, setStartDate] = React.useState(defaultStartDateFormatted);
    const [endDate, setEndDate] = React.useState(defaultEndDateFormatted);
    const [startDateInput, setStartDateInput] = React.useState("");
    const [endDateInput, setEndDateInput] = React.useState("");
    const [search, setSearch] = React.useState("");
    const [searchFocused, setSearchFocused] = React.useState(false);
    const [searchInput, setSearchInput] = React.useState("");
    const [shiftNameFilter, setShiftNameFilter] = React.useState("");
    const [shiftId, setShiftId] = React.useState("");
    const [lastDays, setLastDays] = React.useState("");
    const [filtersCount, setFiltersCount] = useState(0);
    const [shifts, setShifts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statistics, setStatistics] = useState(null);
    const [dataLoading, setDataLoading] = useState(false);
    const [statisticsLoading, setStatisticsLoading] = useState(false);
    const [totalElements, setTotalElements] = useState(0);
    const [needsAggregateRefresh, setNeedsAggregateRefresh] = useState(true);
    const sortRef = React.useRef();
    const filterRef = React.useRef();
    const calendarRef = React.useRef();
    const searchRef = React.createRef();
    const shiftNameFilterRef = React.createRef();
    const [reload, setReload] = React.useState(true);
    const [aiSummaryVisible, setAiSummaryVisible] = React.useState(false);
    const filterKey = [startDate, endDate, search, shiftNameFilter, filters.join(','), sort].join('|');
    const SORTS = [
        {
            key: "NAME ASC",
            title: "Name, A-Z"
        },
        {
            key: "NAME DESC",
            title: "Name, Z-A"
        },
        {
            key: "CPSS ASC",
            title: "CPSS, Ascending"
        },
        {
            key: "CPSS DESC",
            title: "CPSS, Descending"
        }
    ];

    React.useEffect(() => {
        props.getDigestSettings();
        loadShiftsAndFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reload])

    React.useEffect(() => {
        if (!isAiInlineSummarizationEnabled()) return;
        API.get('Core', '/api/v1/ai-administration')
            .then(response => {
                const summaryEnabled = response.find(s => s.flag === 'AI_SUMMARY_SETTING');
                const excludeSatisfaction = response.find(s => s.flag === 'AI_SUMMARY_EXCLUDE_SATISFACTION');
                const isSummaryOn = summaryEnabled ? !!summaryEnabled.enabled : false;
                const isExcluded = excludeSatisfaction ? !!excludeSatisfaction.enabled : false;
                setAiSummaryVisible(isSummaryOn && !isExcluded);
            })
            .catch(() => setAiSummaryVisible(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    React.useEffect(() => {
        if (!loading) {
            loadPage(page);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    React.useEffect(() => {
        if (!loading) {
            // Mark that aggregates need refresh when filters or search change
            setNeedsAggregateRefresh(true);
            loadPage(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, startDate, endDate, shiftNameFilter, search, sort, loading]);

    React.useEffect(() => {
        if (!loading) {
            // When sort changes, reload page but don't refresh aggregates
            loadPage(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sort]);

    const loadShiftsAndFilters = async () => {
        try {
            const [shiftsResponse, filterSettingsResponse] = await Promise.all([
                fetchShifts(),
                fetchInitialFilterSettings()
            ]);
            const {filters, lastDays, startDate, endDate, shiftId, sorting} = filterSettingsResponse;
            let filterCount = 0;
            if (filters && filters.includes("HAS_EMAIL")) filterCount++;
            if (filters && (filters.includes("CALLTAKER") || filters.includes("INVESTIGATOR") || filters.includes("RESPONDER"))) filterCount++;
            setFilters(filters ? (filters.split(',')) : []);
            setLastDays(lastDays);
            if (lastDays) {
                setStartDate(moment().utc().subtract(lastDays, 'day').format(DATE_FORMAT));
                setEndDate(moment().utc().format(DATE_FORMAT));
                filterCount++;
            } else {
                setStartDateInput(startDate ? (filterCount++, startDate) : "");
                setEndDateInput(endDate ? endDate : "");
                setStartDate(startDate ? startDate : defaultStartDateFormatted);
                setEndDate(endDate ? endDate : defaultEndDateFormatted);
            }
            const matchingShift = shiftsResponse.find(shift => shift.id === shiftId);
            if (matchingShift) {
                setShiftNameFilter(matchingShift.name);
                setShiftId(matchingShift.id);
                filterCount++;
            }
            else{
                setShiftNameFilter("");
                setShiftId("");
            }
            if (sorting) {
                setSort(sorting);
            }
            setFiltersCount(filterCount);
            setLoading(false);
        } catch (error) {
            console.error("Error in initializing component data:", error);
            setLoading(false);
        }
    };

    const fetchShifts = async () => {
        try {
            const response = await API.get('Core', '/api/v1/shift');
            setShifts(response);
            return response;
        } catch (error) {
            toast.error(`Could not get shifts: ${error.response?.data?.message || error.message}`, {
                position: "top-center",
            });
        }
    };

    const fetchInitialFilterSettings = async () => {
        try {
            const response = await API.get('Core', '/api/v1/satisfaction/filter');
            return response;
        } catch (error) {
            toast.error(`Failed to fetch filter  settings: ${error.response?.data?.message || error.message}`, {
                position: "top-center",
            });
        }
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleFilterOptionsOpen = () => {
        setFilterOptions(true);
    };

    const handleFilterOptionsClose = () => {
        setFilterOptions(false);
    };

    const handleSaveFilters = () => {
        handleFilterOptionsClose();
        setNeedsAggregateRefresh(true); // Filters changed, need to refresh aggregates
        reloadData();
    };

    const handleClearFilters = () => {
        setNeedsAggregateRefresh(true); // Filters cleared, need to refresh aggregates
        clearAllFilters(false);
    };

    const reloadData = () => {
        setReload(prev => !prev);
    };

    const clearAllFilters = (reset) => {
        const requestBody = {
            filters: "",
            lastDays: null,
            startDate: null,
            endDate: null,
            shiftId: null,
            sorting: sort
        };
        API.put("Core", "/api/v1/satisfaction/filter", {body: requestBody})
            .then(response => {
                setFilters([]);
                setShiftId("");
                setShiftNameFilter("");
                setLastDays("");
                setStartDate(defaultStartDateFormatted);
                setEndDate(defaultEndDateFormatted);
                setFiltersCount(0);
                if (reset) {
                    handleFilterOptionsClose();
                }
                toast.success("All filters reset successfully!", {
                    position: "top-center"
                });
            })
            .catch(error => {
                toast.error("Could not reset filters: " + error.response.data.message, {
                    position: "top-center"
                });
            });
    }
    const toggleFilter = (filter) => {
        if (_.includes(filters, filter)) {
            _.remove(filters, function (remFilter) {
                return remFilter === filter;
            });
            setFilters(Object.assign([], filters));
        } else {
            filters.push(filter);
            setFilters(Object.assign([], filters));
        }
        filterRef.current.close();
    }

    const onSortClick = (chosenSort) => {
        setSort(chosenSort);
        sortRef.current.close();
        const requestBody = {
            filters: filters.join(','),
            lastDays: lastDays ? +lastDays : null,
            startDate: startDateInput || null,
            endDate: endDateInput || null,
            shiftId: shiftId || null,
            sorting: chosenSort
        };
        API.put("Core", "/api/v1/satisfaction/filter", {body: requestBody})
            .catch(error => {
                toast.error("Could not save sort preference: " + (error.response?.data?.message || error.message), {
                    position: "top-center"
                });
            });
    }
    const handleAddSuccess = (response, close) => {
        props.getDigestSettings();
        if (close) {
            handleClose();
        }
    }

    const loadMore = () => {
        setPage(page + 1);
    }

    const keyPress = (e) => {
        if (e.keyCode === 13) {
            setSearch(searchRef.current.value);
        }
    }

    const keyPressShiftName = (e) => {
        if (e.keyCode === 13) {
            setShiftNameFilter(shiftNameFilterRef.current.value);
        }
    }

    const fetchAiSummary = async () => {
        const sortParts = sort ? sort.split(' ') : [];
        const sortKey = sortParts[0] || 'NAME';
        const sortDir = sortParts[1] || 'ASC';
        const queryStringParameters = {
            startDate,
            endDate,
            sort: sortKey,
            sortDirection: sortDir,
        };
        if (search) queryStringParameters.search = search;
        if (shiftNameFilter) queryStringParameters.shiftName = shiftNameFilter;
        if (filters.length) queryStringParameters.filters = filters;

        try {
            const response = await API.post("Core", "/api/v1/ai-summarization/satisfaction", {
                queryStringParameters,
                body: {},
            });
            return {
                success: response.success,
                response: response.response,
                responseType: 'html',
            };
        } catch (err) {
            return {
                success: false,
                response: '<p>Failed to generate summary. Please try again.</p>',
                responseType: 'html',
            };
        }
    };

    const loadPage = (pageNum) => {
        const [sortKey, direction] = sort.split(" ");

        // Set loading state when fetching first page
        if (pageNum === 0) {
            setDataLoading(true);
            // Set statistics loading only when we need to refresh aggregates
            if (needsAggregateRefresh) {
                setStatisticsLoading(true);
            }
        }

        // Build query parameters
        const queryParams = {
            page: pageNum,
            size: 100,
            sort: sortKey,
            sortDirection: direction,
            startDate: startDate,
            endDate: endDate,
            search: search,
            shiftName: shiftNameFilter,
            filters: filters
        };

        // Only include aggregates when needed (initial load, filter changes, search - but not page changes or sort)
        if (needsAggregateRefresh && pageNum === 0) {
            queryParams.includeAggregates = true;
        }

        API.get("Core", "/api/v1/survey-reports/cpss", {
            queryStringParameters: queryParams
        }).then(response => {
            // Handle new response format: { data: {...}, aggregates: {...} }
            const pageData = response.data || response; // Backward compatible
            const aggregates = response.aggregates;

            if (pageNum === 0) {
                setPersonnel(pageData.content);
                setTotalElements(pageData.totalElements || 0);

                // Use backend aggregates if provided, otherwise keep existing or calculate from page
                if (aggregates) {
                    mapAggregatestoStatistics(aggregates, pageData.totalElements);
                    setNeedsAggregateRefresh(false); // Aggregates refreshed, clear flag
                    setStatisticsLoading(false); // Statistics loaded
                } else if (statistics === null) {
                    // Fallback: calculate from page if no aggregates and no existing statistics
                    calculateStatistics(pageData.content, pageData.totalElements);
                    setStatisticsLoading(false); // Statistics loaded
                }
                // else: keep existing statistics (for sort-only changes)
            } else {
                setPersonnel(personnel.concat(pageData.content));
            }
            setPage(pageData.number);
            setHasMore(!pageData.last);
            setDataLoading(false);
        }, err => {
            toast("Could not load satisfaction details: " + err.response.data.message, {
                position: "top-center",
                type: "error"
            });
            setDataLoading(false);
            setStatisticsLoading(false);
        })
    }

    /**
     * Map backend aggregates to statistics format.
     * This is used when aggregates are provided by the backend (page 0 only).
     * @param {Object} aggregates - Aggregates from backend API
     * @param {Number} totalCount - Total number of personnel (Names)
     */
    const mapAggregatestoStatistics = (aggregates, totalCount) => {
        try {
            if (!aggregates) {
                setStatistics(null);
                return;
            }

            // Calculate Survey Response Rate from backend aggregates
            const surveyResponseRate = aggregates.totalNumberSurveys > 0
                ? (aggregates.totalNumberRespondedSurveys / aggregates.totalNumberSurveys) * 100
                : 0;

            // Backend provides average CPSS (0-1), convert to percentage (0-100)
            const combinedCPSS = aggregates.averageCpss * 100;

            setStatistics({
                totalPersonnel: totalCount,
                totalSentSurveys: aggregates.totalNumberSurveys,
                totalRespondedSurveys: aggregates.totalNumberRespondedSurveys,
                surveyResponseRate: surveyResponseRate,
                totalAnswers: aggregates.totalNumberResponses,
                totalPositiveAnswers: aggregates.totalNumberPositiveResponses,
                combinedCPSS: combinedCPSS
            });
        } catch (error) {
            console.error("Failed to map backend aggregates:", error);
            setStatistics(null);
        }
    };

    /**
     * Calculate aggregated statistics from the API response.
     * This is a fallback for backward compatibility when aggregates are not provided.
     * @param {Array} contentArray - The content array from the API response
     * @param {Number} totalCount - Total number of personnel (Names)
     */
    const calculateStatistics = (contentArray, totalCount) => {
        try {
            if (!contentArray || contentArray.length === 0) {
                setStatistics(null);
                return;
            }

            // Aggregate data from all personnel in the response
            let totalSentSurveys = 0;
            let totalRespondedSurveys = 0;
            let totalAnswers = 0;
            let totalPositiveAnswers = 0;
            let totalCPSS = 0;

            contentArray.forEach(person => {
                totalSentSurveys += person.numberSurveys || 0;
                totalRespondedSurveys += person.numberRespondedSurveys || 0;
                totalAnswers += person.numberResponses || 0;
                totalPositiveAnswers += person.numberPositiveResponses || 0;
                totalCPSS += person.cpss || 0;
            });

            // Calculate rates
            // Survey Response Rate = (Total Responded Surveys / Total Sent Surveys) * 100
            const surveyResponseRate = totalSentSurveys > 0
                ? (totalRespondedSurveys / totalSentSurveys) * 100
                : 0;

            // Combined CPSS = (Total CPSS / Number of Personnel) * 100
            const combinedCPSS = contentArray.length > 0
                ? (totalCPSS / contentArray.length) * 100
                : 0;

            setStatistics({
                totalPersonnel: totalCount,
                totalSentSurveys: totalSentSurveys,
                totalRespondedSurveys: totalRespondedSurveys,
                surveyResponseRate: surveyResponseRate,
                totalAnswers: totalAnswers,
                totalPositiveAnswers: totalPositiveAnswers,
                combinedCPSS: combinedCPSS
            });
        } catch (error) {
            console.error("Failed to calculate statistics:", error);
            setStatistics(null);
        }
    };

    let selectedSort = _.find(SORTS, function (s) {
        return s.key === sort;
    });
    return (
        <div className="rootView satisfactionView">
            <div className="navBar">
                <div className="navTitle">
                    <div className="viewName">Personnel Satisfaction</div>
                </div>
                <div className="navActions">
                    <Modal
                        button={(
                            <Button
                                title="none"
                                icon={<AssetButtonSettings/>}
                                size="medium"
                                addClass="settingsButton navAction"
                            />
                        )}
                        content={(<AddEditDigestSettings
                            existingDigestSettings={props.satisfaction.digestSettings}
                            onSuccess={handleAddSuccess}
                        />)}
                        dialogClassName="modalDialog satisfactionDigestPreferences"
                        title="Personnel Email Digest"
                        size="medium"
                        handleClose={handleClose}
                        handleOpen={handleOpen}
                        open={open}
                    />
                </div>

                <div className="separator"></div>
            </div>
            <div className="satisfactionAlignMainView">
                <div className="satisfactionComponentsAlignView">
                    <div
                        className={`searchInput action ${searchFocused ? "isFocused" : ""} ${(searchInput.length > 0) ? "hasInput" : ""}`}
                        style={{marginRight: 10}}>
                        <div className="icon"/>
                        <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                               placeholder={"Search Personnel"} onFocus={() => setSearchFocused(true)}
                               onBlur={() => setSearchFocused(false)} className="input" onKeyDown={keyPress} type="text"
                               ref={searchRef}/>
                        <div className="clear" onClick={() => {
                            setSearch("");
                            setSearchInput("");
                        }}></div>
                    </div>
                    <Popup closeOnDocumentClick ref={sortRef} position={"bottom left"} trigger={<div role="button">
                        <SortButton
                            buttonState=""
                            sortValue={selectedSort ? selectedSort.title : ""}
                            addClass="navAction"
                        />
                    </div>}>
                        <div className={"pullDownMenu"}>
                            {
                                _.map(SORTS, function (s, sIndex) {
                                    return (
                                        <div key={sIndex}
                                             className={`menuCell ${s.key === sort ? `selected sortDirection-${sortDirection}` : ''}`}
                                             onClick={onSortClick.bind(this, s.key)}>
                                            <div className="satisfactionMenuItemTitle">{s.title}</div>
                                        </div>
                                    );
                                })
                            }
                        </div>
                    </Popup>
                    <div className="satisfactionTotalPeople">
                        <span className="totalPeopleCount">{totalElements}</span>
                        <span className="totalPeopleLabel">Names</span>
                    </div>
                </div>

                <div className="satisfactionComponentsAlignView">
                    <div style={{paddingRight: '10px'}}>
                        <button
                            className={filtersCount > 0 ? "button dismiss clearFilterActive" : "button dismiss clearFilterInActive disabled"}
                            onClick={handleClearFilters}
                            disabled={filtersCount === 0}>Clear Filters
                        </button>
                    </div>
                    <Modal
                        button={(
                            <div role="button">
                                <FilterButton
                                    addClass={filtersCount > 0 ? "button confirm medium" : "button dismiss medium"}
                                    title={filtersCount > 0 ? `Filters(${filtersCount})` : "Filters"}
                                />
                            </div>
                        )}
                        content={(<AddEditSatisfactionFilters onSaveFilters={handleSaveFilters}
                                                              onCancel={handleFilterOptionsClose}
                                                              onResetFilter={() => {
                                                                  clearAllFilters(true);
                                                              }}
                                                              shifts={shifts}
                                                              filters={filters}
                                                              lastDaysInput={lastDays}
                                                              startDate={startDateInput}
                                                              endDate={endDateInput}
                                                              shiftId={shiftId}
                                                              currentSort={sort}
                        />)}
                        dialogClassName="modalDialog satisfactionFilters"
                        title="Filters"
                        role="dialog"
                        ariaLabel="satisfactionFilters modal"
                        size="small"
                        handleClose={handleFilterOptionsClose}
                        handleOpen={handleFilterOptionsOpen}
                        open={filterOptions}
                    />
                </div>
            </div>

            {(dataLoading && statisticsLoading) ? (
                // Full page loader for initial load and filter changes
                <div className="scrollView">
                    <div className="tableView">
                        <div className="tableSection" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px'}}>
                            <CircularProgress />
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {aiSummaryVisible && (
                        <div className="satisfactionStatistics" style={{paddingTop: '12px', paddingBottom: '10px'}}>
                            <div style={{maxHeight: '220px', overflowY: 'auto'}}
                                 tabIndex={0}>
                                <AiSummaryBox
                                    key={filterKey}
                                    containerId="satisfaction-ai-summary-box"
                                    onSummaryRequested={fetchAiSummary}
                                />
                            </div>
                        </div>
                    )}
                    {/* Statistics Cards Section */}
                    <SatisfactionStatistics
                        statistics={statistics}
                        loading={false}
                    />

                    {dataLoading ? (
                        // Loader only for personnel table (search/sort)
                        <div className="scrollView">
                            <div className="tableView">
                                <div className="tableSection" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px'}}>
                                    <CircularProgress />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="scrollView">
                            <div className="tableView">
                                <div className="tableSection">
                                    {
                                        personnel.map(function (p) {
                                return (
                                    <SatisfactionPersonnelCell
                                        personnelName={p.name}
                                        key={p.internalPersonnelId}
                                        onCellClick={(e) => {
                                            // If they click anything but a clickable attribute on the row, then ignore it
                                            if (!e.target.classList.contains("personnelCellClickable")) {
                                                return;
                                            }

                                            props.history.push({
                                                pathname: "/satisfaction-personnel/" + p.internalPersonnelId,
                                                search: "?" + new URLSearchParams({
                                                    startDate: startDate,
                                                    endDate: endDate
                                                })
                                            })
                                        }}
                                        personnelRole={p.personnelType}
                                        internalPersonnelId={p.internalPersonnelId}
                                        personnelID={p.personnelId}
                                        onEmailUpdate={() => {
                                            loadPage(page);
                                        }}
                                        personnelEmail={p.email}
                                        surveysCount={p.numberResponses}
                                        positiveFeedbackCount={p.numberPositiveResponses}
                                        satisfactionScore={_.round((p.cpss * 100), 1) + '%'}
                                    />
                                )
                            })
                        }
                                {
                                    hasMore ? (
                                        <PaginationExpanderButton onClick={loadMore}/>
                                    ) : null
                                }
                            </div>
                        </div>
                    </div>
                    )}
                </>
            )}
        </div>
    )
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SatisfactionWithFilterIndex));
